import React, { useState } from 'react';
import { useAuth } from '../services/contexts/AuthContext';
import { LogOut } from 'lucide-react';
import Sidebar from './Sidebar';
import DashboardView from './views/DashboardView';
import PatientsView from './views/PatientsView';
import PlaceholderView from './views/PlaceholderView';

// Import modular components
import ClinicManagement from './admin/ClinicManagement';
import UserManagement from './admin/UserManagement';

import {
  Building2,
  Users,
  Settings,
  Shield,
  FileText,
  Mail,
  Activity,
  UserCog,
  BarChart3,
  Calendar,
  MessageSquare,
  Send,
  Clock,
  Stethoscope,
  Phone,
  Heart,
  TrendingUp,
  History,
  AlertTriangle
} from 'lucide-react';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [activeView, setActiveView] = useState('dashboard');

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return <DashboardView />;
      
      case 'patients':
        return <PatientsView />;
      
      // System Admin Views
      case 'tenants':
        return <ClinicManagement />;
      case 'users':
        return <UserManagement />;
      case 'configurations':
        return <PlaceholderView title="System Configuration" icon={Settings} description="Configure system-wide settings, API keys, and integration parameters." />;
      case 'security':
        return <PlaceholderView title="Security & Authentication" icon={Shield} description="Manage security policies, authentication settings, and access controls." />;
      case 'audit':
        return <PlaceholderView title="Audit Logs" icon={FileText} description="View comprehensive audit trails and system activity logs." />;
      case 'email-templates':
        return <PlaceholderView title="Email Templates" icon={Mail} description="Manage email templates for patient communications and system notifications." />;
      case 'system-health':
        return <PlaceholderView title="System Health" icon={Activity} description="Monitor system performance, uptime, and resource utilization." />;
      
      // Clinic Head Views
      case 'staff':
        return <PlaceholderView title="Staff Management" icon={UserCog} description="Manage clinic staff, roles, and assignments." />;
      case 'vitals-overview':
        return <PlaceholderView title="Vitals Overview" icon={Heart} description="Overview of all patient vital signs and health metrics." />;
      case 'analytics':
        return <PlaceholderView title="Analytics & Reports" icon={BarChart3} description="Generate reports and analyze clinic performance metrics." />;
      case 'scheduling':
        return <PlaceholderView title="Recording Schedule" icon={Calendar} description="Manage and schedule patient vital sign recordings." />;
      case 'compliance':
        return <PlaceholderView title="Compliance Review" icon={Shield} description="Review compliance status and audit requirements." />;
      case 'notifications':
        return <PlaceholderView title="Notifications" icon={MessageSquare} description="Manage clinic notifications and communication settings." />;
      case 'clinic-settings':
        return <PlaceholderView title="Clinic Settings" icon={Settings} description="Configure clinic-specific settings and preferences." />;
      
      // Clinic Technician Views
      case 'send-links':
        return <PlaceholderView title="Send Recording Links" icon={Send} description="Send recording links to patients via email or SMS." />;
      case 'schedule':
        return <PlaceholderView title="My Schedule" icon={Calendar} description="View and manage your daily schedule and appointments." />;
      case 'pending-recordings':
        return <PlaceholderView title="Pending Recordings" icon={Clock} description="Track patients with pending vital sign recordings." />;
      case 'recent-vitals':
        return <PlaceholderView title="Recent Vitals" icon={Stethoscope} description="View recently completed vital sign recordings." />;
      case 'patient-support':
        return <PlaceholderView title="Patient Support" icon={Phone} description="Access patient support tools and communication history." />;
      case 'my-activity':
        return <PlaceholderView title="My Activity" icon={Activity} description="Track your daily activities and performance metrics." />;
      
      // Patient Views
      case 'latest-vitals':
        return <PlaceholderView title="Latest Vitals" icon={Activity} description="View your most recent vital sign measurements." />;
      case 'history':
        return <PlaceholderView title="Health History" icon={History} description="Access your complete health history and trends." />;
      case 'trends':
        return <PlaceholderView title="Health Trends" icon={TrendingUp} description="Visualize your health trends and progress over time." />;
      case 'recordings':
        return <PlaceholderView title="My Recordings" icon={Calendar} description="View and manage your vital sign recording schedule." />;
      case 'alerts':
        return <PlaceholderView title="Health Alerts" icon={AlertTriangle} description="View important health alerts and notifications." />;
      case 'profile':
        return <PlaceholderView title="My Profile" icon={Settings} description="Manage your personal information and preferences." />;
      
      default:
        return <DashboardView />;
    }
  };

  const getPageTitle = () => {
    const titles = {
      dashboard: 'Dashboard',
      patients: user?.role === 'patient' ? 'My Health' : 'Patients',
      tenants: 'Clinic Management',
      users: 'User Management',
      configurations: 'System Configuration',
      security: 'Security & Auth',
      audit: 'Audit Logs',
      'email-templates': 'Email Templates',
      'system-health': 'System Health',
      staff: 'Staff Management',
      'vitals-overview': 'Vitals Overview',
      analytics: 'Analytics & Reports',
      scheduling: 'Recording Schedule',
      compliance: 'Compliance Review',
      notifications: 'Notifications',
      'clinic-settings': 'Clinic Settings',
      'send-links': 'Send Recording Links',
      schedule: 'My Schedule',
      'pending-recordings': 'Pending Recordings',
      'recent-vitals': 'Recent Vitals',
      'patient-support': 'Patient Support',
      'my-activity': 'My Activity',
      'latest-vitals': 'Latest Vitals',
      history: 'Health History',
      trends: 'Health Trends',
      recordings: 'My Recordings',
      alerts: 'Health Alerts',
      profile: 'My Profile'
    };
    return titles[activeView] || 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      {/* Modern Sidebar */}
      <Sidebar activeView={activeView} setActiveView={setActiveView} />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Modern Top Bar */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 h-16 flex items-center justify-between px-6 shadow-soft">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-bold text-gray-900">{getPageTitle()}</h1>
            <div className="hidden md:flex items-center space-x-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-500">Live</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Notification Bell */}
            <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM19 12H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v5a2 2 0 01-2 2z" />
              </svg>
              <div className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></div>
            </button>
            
            {/* Modern Logout Button */}
            <button
              onClick={logout}
              className="flex items-center space-x-2 text-gray-500 hover:text-gray-700 px-3 py-2 rounded-xl hover:bg-gray-100 transition-all duration-200 group"
              title="Logout"
            >
              <LogOut className="h-4 w-4 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium hidden sm:block">Logout</span>
            </button>
          </div>
        </header>

        {/* Main Content Area with Animation */}
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="animate-fade-in">
            {renderView()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;